
import { FONT_MONO } from 'styles/tokens/typography';
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  Button,
  Switch,
  FormControlLabel,
  Paper,
  CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material';
import MDTypography from "components/MDTypography";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import DOMPurify from 'dompurify';
import { useAuth } from 'auth/Auth';
import {
  buildProfilePhotoHtml,
  imageUrlToDataUri,
} from 'utils/emailSignature';

const EmailSignatureEditor = ({ onSignatureSave }) => {
  const [signature, setSignature] = useState('');
  const [showHtmlEditor, setShowHtmlEditor] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [insertingPhoto, setInsertingPhoto] = useState(false);
  const { axiosInstance, user, refreshUserAvatar } = useAuth();
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  useEffect(() => {
    fetchSignature();
  }, []);

  const fetchSignature = async () => {
    try {
      const response = await axiosInstance.get('/email-settings/signature');
      setSignature(response.data.signature || '');
    } catch (error) {
      console.error('Error fetching signature:', error);
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const response = await axiosInstance.put('/email-settings/signature', {
        signature: signature
      });
      const saved = response.data?.signature ?? signature;
      setSignature(saved);
      if (onSignatureSave) {
        onSignatureSave(saved);
      }
      showSnackbar('Signature saved successfully!');
    } catch (error) {
      console.error('Error saving signature:', error);
      showSnackbar(error.response?.data?.detail || 'Failed to save signature', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleInsertProfilePhoto = async () => {
    try {
      setInsertingPhoto(true);
      let avatarUrl = user?.avatar || '';
      if (typeof refreshUserAvatar === 'function') {
        const refreshed = await refreshUserAvatar();
        if (refreshed) avatarUrl = refreshed;
      }
      if (!avatarUrl) {
        showSnackbar('No profile photo found. Upload one in Edit Profile first.', 'warning');
        return;
      }

      const embeddedUrl = await imageUrlToDataUri(avatarUrl);
      const photoHtml = buildProfilePhotoHtml(embeddedUrl, user?.full_name || 'Profile photo');
      if (!photoHtml) {
        showSnackbar('Could not load profile photo for signature.', 'error');
        return;
      }

      setSignature((prev) => {
        const body = prev || '';
        if (body.includes(embeddedUrl) || (avatarUrl && body.includes(avatarUrl))) {
          return body;
        }
        if (!body.trim()) {
          return `<p>${photoHtml}</p>`;
        }
        return `${body}<p><br></p><p>${photoHtml}</p>`;
      });
      showSnackbar('Profile photo inserted. Save the signature to keep it.');
    } catch (error) {
      console.error('Error inserting profile photo:', error);
      showSnackbar('Failed to insert profile photo', 'error');
    } finally {
      setInsertingPhoto(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const modules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      ['link', 'image'],
      ['clean']
    ]
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Card sx={{ p: 3 }}>
      <MDTypography variant="h6" gutterBottom>
        Email Signature
      </MDTypography>

      <Box sx={{ mb: 2 }}>
        <FormControlLabel
          control={
            <Switch
              checked={showHtmlEditor}
              onChange={(e) => setShowHtmlEditor(e.target.checked)}
            />
          }
          label="HTML Mode"
        />
        <FormControlLabel
          control={
            <Switch
              checked={showPreview}
              onChange={(e) => setShowPreview(e.target.checked)}
            />
          }
          label="Preview"
        />
      </Box>

      {showPreview ? (
        <Paper sx={{ p: 2, mb: 2, minHeight: '200px' }}>
          <div
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(signature, {
                ADD_ATTR: ['target'],
                ADD_DATA_URI_TAGS: ['img'],
              })
            }}
          />
        </Paper>
      ) : showHtmlEditor ? (
        <textarea
          value={signature}
          onChange={(e) => setSignature(e.target.value)}
          style={{
            width: '100%',
            minHeight: '200px',
            fontFamily: FONT_MONO,
            padding: '8px',
            marginBottom: '16px'
          }}
        />
      ) : (
        <Box sx={{ height: '300px', mb: 3 }}>
          <ReactQuill
            value={signature}
            onChange={setSignature}
            modules={modules}
            style={{ height: '100%' }}
          />
        </Box>
      )}

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: showPreview || showHtmlEditor ? 0 : 2 }}>
        <Button
          variant="outlined"
          onClick={handleInsertProfilePhoto}
          disabled={insertingPhoto || saving}
        >
          {insertingPhoto ? 'Inserting…' : 'Insert profile photo'}
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || insertingPhoto}
          sx={{ color: '#fff' }}
        >
          {saving ? 'Saving...' : 'Save Signature'}
        </Button>
      </Box>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Card>
  );
};

export default EmailSignatureEditor;
